import React, { useEffect, useState } from 'react';
import Bread from 'modules/layoutModule/components/Bread/Bread';
import { HomeOutlined, EditOutlined, DeleteFilled } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import {
  Breadcrumb,
  Pagination,
  Popconfirm,
  ConfigProvider,
  BackTop,
} from 'antd';
import { Table, Space, Tooltip, Button } from 'antd';
import TableDateRow from 'modules/layoutModule/components/TableDateRow/TableDateRow';
import plPL from 'antd/lib/locale/pl_PL';
import moment from 'moment';
import TableDataRowTypes from 'modules/layoutModule/enums/TableDataRowTypes';
import useColumnSearch from 'modules/layoutModule/hooks/useColumnSearch';
import {
  getSectionsRequest,
  removeSpecificSectionRequest,
} from '../requests/sectionsRequests';

export interface NewsTableTypes {
  id: number;
  dateAdd: moment.MomentInput;
  name: string;
  isActive: number;
  nawozyTVCount: number;
}

const Sections = (): JSX.Element => {
  const [tableData, setTableData] = useState<NewsTableTypes[]>([]);
  const [loading, toggleLoading] = useState<boolean>(true);
  const [searchedColumn, setSearchedColumn] = useState('');

  const confirmDelete = (id: number) => {
    removeSpecificSectionRequest(id).then(() => {
      downloadSections();
    });
  };

  const downloadSections = () =>
    getSectionsRequest().then((response) => {
      if (response) {
        setTableData(response);
        toggleLoading(false);
      } else {
        toggleLoading(false);
      }
    });

  useEffect(() => {
    if (tableData.length === 0) {
      downloadSections();
    }
  }, [tableData]);

  const columns = [
    {
      title: 'Nazwa',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: { name: string }, b: { name: string }) =>
        a.name.localeCompare(b.name),
      ...useColumnSearch(
        'name',
        'Szukaj po tytule',
        'Szukaj',
        'Resetuj',
        searchedColumn,
        setSearchedColumn
      ),
    },
    {
      title: 'Nazwa skrócona',
      dataIndex: 'nameShort',
      key: 'nameShort',
      sorter: (a: { nameShort: string }, b: { nameShort: string }) =>
        a.nameShort.localeCompare(b.nameShort),
      ...useColumnSearch(
        'nameShort',
        'Szukaj po tytule',
        'Szukaj',
        'Resetuj',
        searchedColumn,
        setSearchedColumn
      ),
    },
    {
      title: 'Aktywna',
      dataIndex: 'isActive',
      key: 'isActive',
      width: '150px',
      render: (text: string, record: any) => (
        <TableDateRow record={record} field={TableDataRowTypes.IS_ACTIVE} />
      ),
      sorter: (a: { isActive: number }, b: { isActive: number }) =>
        a.isActive - b.isActive,
      filters: [
        {
          text: 'TAK',
          value: 'isActive',
        },
        {
          text: 'NIE',
          value: 'isNotActive',
        },
      ],
      onFilter: (value: string, record: { isActive: boolean }) => {
        if (value === 'isActive') {
          return record.isActive;
        }
        return !record.isActive;
      },
    },
    {
      title: 'Kolejność',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      width: 100,
      sorter: (a: any, b: any) => a.sortOrder - b.sortOrder,
      render: (text: string, record: any) => (
        <TableDateRow record={record} field={TableDataRowTypes.SORT} />
      ),
    },
    {
      title: 'Akcje',
      dataIndex: 'operation',
      key: 'operation',
      width: 100,
      render: (text: string, item: NewsTableTypes) => (
        <Space
          size="middle"
          style={{ display: 'Flex', flexDirection: 'column' }}
        >
          <Tooltip title="Edytuj wpis">
            <Link to={`/sections/${item.id}`}>
              <Button type="primary" shape="circle" icon={<EditOutlined />} />
            </Link>
          </Tooltip>
          <Tooltip title="Usuń wpis">
            <Popconfirm
              title="Czy na pewno chcesz usunąc ten wpis?"
              placement="left"
              onConfirm={() => confirmDelete(item.id)}
              okText="Tak"
              cancelText="Nie"
            >
              <Button
                type="link"
                disabled={item.isActive === 1 || item.nawozyTVCount > 0}
              >
                <DeleteFilled />
              </Button>
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Bread>
        <>
          <Breadcrumb.Item>
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>Ustawienia treści</Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to="/sections">Wiadomości regiony</Link>
          </Breadcrumb.Item>
        </>
      </Bread>
      <ConfigProvider locale={plPL}>
        <Table
          // @ts-ignore
          columns={columns}
          // @ts-ignore
          dataSource={tableData}
          loading={loading}
          rowKey={(record) => record.id}
        >
          <Pagination />
        </Table>
        <BackTop />
      </ConfigProvider>
    </>
  );
};

export default Sections;
